import React, {ReactElement} from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  CardContent,
  Typography,
  makeStyles
} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
import {appointmentsDateTimeFormat} from '@shared/utils/DateTimeUtils';
import {useSelector} from 'react-redux';
import PhoneNumberFormatter from '@components/system/PhoneNumberFormatter/PhoneNumberFormatter';
import {RequestingProvider} from '../EligibilityVerificationMapper';
import KeyValueCard from '@components/system/KeyValueCard/KeyValueCard';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import {CARD_BOX_SHADOW} from 'src/style/variable';

const insuranceProviderDetailsCard = makeStyles(() => ({
  cardRoot: {
    '& .MuiCardContent-root:last-child': {
      paddingBottom: '13px'
    },
    flex: 1,
    boxShadow: CARD_BOX_SHADOW,
    borderRadius: '1rem'
  },
  section: {
    display: 'flex',
    flexDirection: 'column'
  },
  title: {
    fontSize: '1.3rem',
    fontWeight: 600
  },
  contactInfoWrapper: {
    paddingBlock: '0.5rem'
  },
  benefitAccordionSummary: {
    flexDirection: 'row-reverse',
    paddingLeft: 0,
    '& .MuiAccordionSummary-content': {
      marginLeft: '1rem'
    }
  }
}));

interface InsuranceProviderDetailsCardInterface {
  requestingProvider: RequestingProvider;
}

const InsuranceProviderDetailsCard = (
  props: InsuranceProviderDetailsCardInterface
): ReactElement => {
  const classes = insuranceProviderDetailsCard();
  const zone = useSelector((state: any) => state.clinics.zone);
  const {requestingProvider} = props;
  const {t} = useTranslation();
  const providerData = [
    {
      label: t('ELIGIBILITY_VERIFICATION.NAME'),
      value: requestingProvider?.name ? requestingProvider.name : '-'
    },
    {
      label: t('ELIGIBILITY_VERIFICATION.CATEGORY'),
      value: requestingProvider?.category ? requestingProvider.name : '-'
    },
    {
      label: t('ELIGIBILITY_VERIFICATION.NPI'),
      value: requestingProvider?.npi ? requestingProvider.npi : '-'
    },
    {
      label: t('ELIGIBILITY_VERIFICATION.TAX_ID'),
      value: requestingProvider?.taxId ? requestingProvider.taxId : '-'
    },
    {
      label: t('ELIGIBILITY_VERIFICATION.PRIMARY_CARE_PROVIDER_START_DATE'),
      value: requestingProvider?.primaryCareProviderStartDate
        ? appointmentsDateTimeFormat(
            requestingProvider?.primaryCareProviderStartDate,
            zone
          )
        : '-'
    },
    {
      label: t('ELIGIBILITY_VERIFICATION.PRIMARY_CARE_PROVIDER_END_DATE'),
      value: requestingProvider?.primaryCareProviderEndDate
        ? appointmentsDateTimeFormat(
            requestingProvider?.primaryCareProviderStartDate,
            zone
          )
        : '-'
    },
    {
      label: t('ELIGIBILITY_VERIFICATION.CONTACT_INFORMATION'),
      value:
        requestingProvider?.contactInformation &&
        Array.isArray(requestingProvider?.contactInformation) &&
        requestingProvider?.contactInformation?.length > 0
          ? requestingProvider?.contactInformation?.map((info, idx) => {
              const contactData = [
                {
                  label: t('ELIGIBILITY_VERIFICATION.NAME'),
                  value: info?.name || '-'
                },
                {
                  label: t('ELIGIBILITY_VERIFICATION.PHONE'),
                  value: info?.phone ? (
                    <PhoneNumberFormatter value={info?.phone} />
                  ) : (
                    '-'
                  )
                },
                {
                  label: t('ELIGIBILITY_VERIFICATION.EMAIL'),
                  value: info?.emailAddress || '-'
                }
              ];
              return (
                <div
                  key={idx}
                  className={classes.contactInfoWrapper}
                  style={{
                    borderTop: idx > 0 ? '1px solid lightgrey' : ''
                  }}>
                  <KeyValueCard data={contactData} />
                </div>
              );
            })
          : '-'
    }
  ];

  return (
    <div className={`section-header ${classes.section}`}>
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          className={classes.benefitAccordionSummary}>
          <Typography className={classes.title}>
            {t('ELIGIBILITY_VERIFICATION.PROVIDER')}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Card className={`${classes.cardRoot} card`}>
            <CardContent>
              <KeyValueCard data={providerData} />
            </CardContent>
          </Card>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default InsuranceProviderDetailsCard;
